import React, { useContext, useEffect, useState } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import {
  adoptServerCheckoutAttempt,
  discardCheckoutAttempt,
  getActiveCheckoutV2,
} from '../services/paymentV2';

/**
 * Surfaces the server-owned checkout pointer after storage loss or login on a
 * second device. Local storage is only a verified cache; it never decides
 * whether another PaymentIntent may be created.
 */
export default function ActiveCheckoutRecovery() {
  const { user, loading } = useContext(AuthContext);
  const userId = user?.uid || null;
  const navigate = useNavigate();
  const [activeCheckout, setActiveCheckout] = useState(null);
  const [storageError, setStorageError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (loading) return () => { cancelled = true; };
    if (!userId) {
      setActiveCheckout(null);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const functions = getFunctions(getApp(), 'us-central1');
        const recovered = await getActiveCheckoutV2(functions);
        if (cancelled) return;
        if (!recovered.active) {
          discardCheckoutAttempt();
          setActiveCheckout(null);
          setStorageError(false);
          return;
        }
        try {
          adoptServerCheckoutAttempt(recovered);
          setStorageError(false);
        } catch {
          // The server binding remains authoritative. Do not permit payment
          // presentation when this browser cannot persist its recovery cache.
          setStorageError(true);
        }
        setActiveCheckout(recovered);
      } catch {
        // A transient discovery failure must not erase a local marker or claim
        // there is no active checkout. The normal checkout callables still
        // enforce the server pointer.
      }
    })();

    return () => { cancelled = true; };
  }, [loading, userId]);

  if (!activeCheckout) return null;
  const terminalPending = [
    'paid',
    'authorized',
    'completed',
  ].includes(activeCheckout.state);
  const canResume = activeCheckout.recoverable && !storageError;
  const label = storageError
    ? 'A secure checkout is active, but this browser cannot save recovery state.'
    : terminalPending
      ? activeCheckout.paymentCollection === 'not_required'
        ? 'Your order is being finalized.'
        : 'Your payment was received and the order is being finalized.'
      : activeCheckout.recoverable
        ? 'You have a secure checkout in progress.'
        : 'Your previous checkout is being safely reconciled.';

  const destination = terminalPending
    ? `/order-confirmation?quoteId=${encodeURIComponent(activeCheckout.quoteId)}`
    : `/checkout?resumeQuoteId=${encodeURIComponent(activeCheckout.quoteId)}`;

  return (
    <div
      role="status"
      style={{
        margin: '12px auto',
        maxWidth: 760,
        padding: '10px 14px',
        border: '1px solid var(--color-border, #d1d5db)',
        borderRadius: 8,
        background: 'var(--secondary-bg, #f8fafc)',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{label}</span>
      {(terminalPending || canResume) && (
        <button
          type="button"
          onClick={() => navigate(destination)}
          style={{ padding: '7px 12px', whiteSpace: 'nowrap', cursor: 'pointer' }}
        >
          {terminalPending ? 'View Order' : 'Resume Checkout'}
        </button>
      )}
    </div>
  );
}
